/**
 * Atom: IconButton
 *
 * Square, icon-only button. Replaces the ad-hoc icon buttons scattered
 * across organisms (TasksTable gear, DataTable resize, close chips, etc.).
 *
 * Renders as <button> by default; passing `href` renders as <a> with
 * the same geometry. This keeps Hub-style nav anchors that previously
 * used `<a className="btn ghost icon">` on the same atom.
 *
 * aria-label is REQUIRED (enforced by the type) — icon-only controls must
 * be announced to screen readers (UI_GUIDELINES §10).
 *
 * Usage:
 *   <IconButton aria-label="Edit task" onClick={...}><Pencil size={16}/></IconButton>
 *   <IconButton variant="ghost" size="sm" aria-label="Close"><X size={14}/></IconButton>
 *   <IconButton href={`/tenants/${id}`} aria-label="Open tenant"><Icon name="arrow-up-right" size={11}/></IconButton>
 */
import React from 'react'
import { tokens } from '@/design-system/tokens'

type Variant = 'ghost' | 'secondary' | 'primary' | 'danger'
type Size = 'xs' | 'sm' | 'md' | 'lg'

interface CommonProps {
  'aria-label': string
  variant?: Variant
  size?: Size
  active?: boolean
}

type ButtonOnlyProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> &
  CommonProps & { href?: undefined }
type AnchorOnlyProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'aria-label'> &
  CommonProps & { href: string }

type IconButtonProps = ButtonOnlyProps | AnchorOnlyProps

// D1a — handoff calls for 26/32/40 square. We keep an `xs` (22) for legacy
// chip sites that haven't been audited yet.
const SIZE_PX: Record<Size, number> = { xs: 22, sm: 26, md: 32, lg: 40 }

const variantStyle = (v: Variant, active: boolean): React.CSSProperties => {
  if (active) {
    return {
      background: 'var(--bg-surface)',
      color: tokens.color.brand.gold,
      border: `1px solid ${tokens.color.brand.gold}`,
    }
  }
  switch (v) {
    case 'primary':
      return {
        background: tokens.color.brand.fluoro,
        color: tokens.color.brand.navy,
        border: `1px solid ${tokens.color.brand.fluoroDark}`,
      }
    case 'secondary':
      return {
        background: 'var(--bg-elevated)',
        color: 'var(--text-primary)',
        border: `1px solid ${tokens.color.border.default}`,
      }
    case 'danger':
      return {
        background: 'transparent',
        color: tokens.color.feedback.error,
        border: '1px solid transparent',
      }
    case 'ghost':
    default:
      return {
        background: 'transparent',
        color: 'var(--text-secondary)',
        border: '1px solid transparent',
      }
  }
}

export const IconButton: React.FC<IconButtonProps> = (props) => {
  const {
    variant = 'ghost',
    size = 'md',
    active = false,
    style,
    children,
  } = props
  const px = SIZE_PX[size]
  const base: React.CSSProperties = {
    width: px,
    height: px,
    // index.css sets a global `button { min-height: 36px }` touch-target rule;
    // every size here (xs/sm/md) is smaller than that, so without overriding
    // min-height/min-width the browser clamps this button's rendered height to
    // 36px while width stays at `px`, distorting the circle/square into an oval.
    minWidth: 0,
    minHeight: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    padding: 0,
    borderRadius: tokens.radius.md,
    cursor: 'pointer',
    textDecoration: 'none',
    transition: `background ${tokens.transition.base}, color ${tokens.transition.base}, border-color ${tokens.transition.base}`,
    outline: 'none',
    ...variantStyle(variant, active),
    ...style,
  }

  const sharedCss = (
    <style>{`
      .ml-iconbtn-D1a:focus-visible {
        outline: 2px solid var(--brand-gold);
        outline-offset: 2px;
      }
      .ml-iconbtn-D1a:not([disabled]):hover {
        filter: brightness(1.1);
      }
    `}</style>
  )

  if ('href' in props && props.href !== undefined) {
    const { variant: _v, size: _s, active: _a, style: _st, children: _c, ...anchorRest } =
      props as AnchorOnlyProps
    return (
      <>
        {sharedCss}
        <a style={base} className="ml-iconbtn-D1a" {...anchorRest}>
          {children}
        </a>
      </>
    )
  }

  const { variant: _v, size: _s, active: _a, style: _st, children: _c, disabled, ...buttonRest } =
    props as ButtonOnlyProps
  return (
    <>
      {sharedCss}
      <button
        type="button"
        disabled={disabled}
        style={{ ...base, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
        className="ml-iconbtn-D1a"
        {...buttonRest}
      >
        {children}
      </button>
    </>
  )
}

export default IconButton
